import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Sparkles, Plus, Trash2, Play, ShoppingCart, Package, Factory, Boxes, TrendingDown, TrendingUp, ClipboardList, ChevronsUpDown, Check, Search } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useNavigate } from "react-router-dom";

// Every production stage between Raw Material and Finished Goods, in process
// order — matches `stage_qty` on each MRP row (backend/server.py `run_mrp`).
const STAGE_COLS = [
  { key: "machine_shop", label: "Machine Shop" },
  { key: "impregnation", label: "Impregnation" },
  { key: "fabrication", label: "Fabrication" },
  { key: "powder_coating", label: "Powder Coating" },
  { key: "semi_finished", label: "Semi Finished" },
  { key: "assembly", label: "Assembly" },
  { key: "testing", label: "Testing" },
  { key: "final_inspection", label: "Final Inspection" },
];

// One filter tab per stage of the pipeline (plus Raw Material and Finished
// Goods at either end, and an All tab). Each tab's `getQty` reads the
// quantity a row holds at that stage; the tab shows only rows where that
// quantity is non-zero.
const STAGE_FILTERS = [
  { key: "ALL", label: "All" },
  { key: "RAW", label: "Raw Material", getQty: (r) => r.rm_store },
  ...STAGE_COLS.map((c) => ({ key: c.key, label: c.label, getQty: (r) => r.stage_qty?.[c.key] })),
  { key: "FG", label: "Finished Goods", getQty: (r) => r.fg_store },
  { key: "REWORK", label: "Rework", getQty: (r) => r.rework_store },
];

export default function MRPPage() {
  const [items, setItems] = useState([]);
  const [demands, setDemands] = useState([]);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [autoCreatePR, setAutoCreatePR] = useState(true);
  const [includeOpenSOs, setIncludeOpenSOs] = useState(true);
  const [typeFilter, setTypeFilter] = useState("ALL");
  // Real-time search by item code or name, narrows the Finished Good pickers below
  const [itemSearch, setItemSearch] = useState("");
  // Real-time filter on the results table by Part Number (code), independent
  // of the item search above — applies on top of the FG/SFG/RAW type filter.
  const [partNumberFilter, setPartNumberFilter] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/items");
      const fgs = data.filter((i) => i.type !== "RAW");
      setItems(data);
      setDemands(fgs.length ? [{ item_id: fgs[0].id, qty: 100 }] : []);
    })();
  }, []);

  const fgItems = items.filter((i) => i.type !== "RAW");
  const iq = itemSearch.trim().toLowerCase();
  const visibleFgItems = iq
    ? fgItems.filter((i) => (i.code || "").toLowerCase().includes(iq) || (i.name || "").toLowerCase().includes(iq))
    : fgItems;

  // As the user types a part number into the top search bar, reflect the
  // match straight into the (sole) demand row's Finished Good field — no
  // need to also open the picker and click it. A full part number is often
  // also a substring of other codes/names (revisions, size variants, other
  // BOM lines), so requiring the search to narrow to a single visible item
  // would silently do nothing for an otherwise-exact code — prefer an exact
  // code match first, and only fall back to "narrowed to one" when there
  // isn't one.
  useEffect(() => {
    if (!iq) return;
    const exact = fgItems.find((i) => (i.code || "").toLowerCase() === iq);
    const match = exact || (visibleFgItems.length === 1 ? visibleFgItems[0] : null);
    if (!match) return;
    setDemands((d) => (d.length === 1 && d[0].item_id !== match.id ? [{ ...d[0], item_id: match.id }] : d));
  }, [iq, fgItems, visibleFgItems]);

  const addRow = () => setDemands((d) => [...d, { item_id: visibleFgItems[0]?.id || fgItems[0]?.id || "", qty: 100 }]);
  const setRow = (i, patch) => setDemands((d) => d.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => setDemands((d) => d.filter((_, idx) => idx !== i));

  const runMRP = async () => {
    setRunning(true);
    try {
      const cleanDemands = demands.filter((d) => d.item_id && d.qty > 0);
      const { data } = await api.post("/mrp/run", {
        demands: cleanDemands,
        horizon_days: 30,
        auto_create_pr: autoCreatePR,
        include_open_sales_orders: includeOpenSOs,
      });
      setResult(data);
      if (data.purchase_request) {
        toast.success(`MRP complete · ${data.purchase_request.code} auto-created`);
      } else {
        toast.success("MRP run complete");
      }
      const sa = data.stock_alerts || {};
      const raised = (sa.new || 0) + (sa.repeat || 0);
      if (raised > 0) {
        toast.warning(`${raised} item(s) at/below minimum stock · ${(sa.emails || []).length} alert email(s) sent/queued`, {
          action: { label: "View", onClick: () => navigate("/stock-availability") },
        });
      }
    } catch {
      toast.error("MRP failed");
    } finally {
      setRunning(false);
    }
  };

  const pnf = partNumberFilter.trim().toLowerCase();
  const activeStageFilter = STAGE_FILTERS.find((f) => f.key === typeFilter);
  const filteredRows = result
    ? result.material_requirements
        .filter((r) => typeFilter === "ALL" || (activeStageFilter?.getQty(r) || 0) !== 0)
        .filter((r) => !iq || (r.code || "").toLowerCase().includes(iq) || (r.name || "").toLowerCase().includes(iq))
        .filter((r) => !pnf || (r.code || "").toLowerCase().includes(pnf))
    : [];

  return (
    <div className="space-y-6" data-testid="mrp-page">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Planning</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
          MRP Run
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          End-to-end view: Raw Material stock · every production stage (Machine Shop → Impregnation → Fabrication → Powder Coating → Semi Finished → Assembly → Testing → Final Inspection) · Finished Goods stock · Open POs (incoming) · Open Transfer Orders (outgoing, with SO Nos) · Net Requirement.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Demand Input</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                placeholder="Search by item code or name…"
                className="pl-8 w-64 h-9"
                data-testid="mrp-item-search"
              />
              {itemSearch && (
                <button
                  onClick={() => setItemSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider text-slate-400 hover:text-slate-700"
                  data-testid="mrp-item-search-clear"
                >
                  Clear
                </button>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={addRow} data-testid="add-demand-btn">
              <Plus className="w-4 h-4 mr-1" />Add demand
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          {demands.map((d, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`demand-row-${idx}`}>
              <div className="col-span-8">
                <Label className="text-[10px] uppercase">Finished Good</Label>
                <FgItemPicker
                  value={d.item_id}
                  selected={fgItems.find((it) => it.id === d.item_id)}
                  items={visibleFgItems}
                  onPick={(id) => setRow(idx, { item_id: id })}
                  testId={`demand-item-${idx}`}
                />
              </div>
              <div className="col-span-3">
                <Label className="text-[10px] uppercase">Quantity</Label>
                <Input type="number" value={d.qty} onChange={(e) => setRow(idx, { qty: parseFloat(e.target.value || 0) })} />
              </div>
              <div className="col-span-1">
                <Button variant="ghost" size="icon" onClick={() => removeRow(idx)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex items-center gap-3 border-t border-slate-100 pt-4">
          <Switch checked={includeOpenSOs} onCheckedChange={setIncludeOpenSOs} data-testid="include-open-so-toggle" id="include-so" />
          <label htmlFor="include-so" className="text-sm text-slate-700 cursor-pointer select-none">
            Also pull demand from <strong>open Transfer Orders</strong>
            <span className="text-slate-500"> (Order Raised · In Production · Confirmed · Ready)</span>
          </label>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <Switch checked={autoCreatePR} onCheckedChange={setAutoCreatePR} data-testid="auto-pr-toggle" id="auto-pr" />
          <label htmlFor="auto-pr" className="text-sm text-slate-700 cursor-pointer select-none">
            Auto-create Purchase Request for raw-material planned orders <span className="text-slate-500">(respects MOQ)</span>
          </label>
        </div>

        <Button onClick={runMRP} disabled={running} className="mt-5 bg-blue-600 hover:bg-blue-700" data-testid="run-mrp-btn">
          <Play className="w-4 h-4 mr-1.5" /> {running ? "Running..." : "Run MRP"}
        </Button>
      </div>

      {result && (
        <>
          {/* Totals strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="mrp-totals">
            <TotalCard icon={ClipboardList} label="Gross Req" value={result.totals?.gross_requirement} tone="slate" />
            <TotalCard icon={Package} label="RM Store" value={result.totals?.rm_store} tone="amber" />
            <TotalCard icon={Factory} label="WIP" value={result.totals?.wip} tone="blue" />
            <TotalCard icon={Factory} label="Rework" value={result.totals?.rework_store} tone="orange" />
            <TotalCard icon={Boxes} label="FG Store" value={result.totals?.fg_store} tone="emerald" />
            <TotalCard icon={TrendingUp} label="On-Order In" value={result.totals?.on_order_incoming} tone="indigo" />
            <TotalCard icon={TrendingDown} label="On-Order Out" value={result.totals?.on_order_outgoing} tone="rose" />
            <TotalCard icon={Sparkles} label="Net Req" value={result.totals?.net_requirement} tone="red" strong />
          </div>

          {result.purchase_request && (
            <div className="bg-white border border-blue-200 rounded-lg p-5 flex items-center justify-between" data-testid="mrp-pr-created">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center">
                  <ShoppingCart className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Auto-generated</div>
                  <div className="text-sm font-semibold text-slate-800">
                    Purchase Request {result.purchase_request.code} created — {result.purchase_request.lines.length} line(s)
                  </div>
                </div>
              </div>
              <Button variant="outline" onClick={() => navigate("/purchase-requests")} data-testid="view-pr-btn">View</Button>
            </div>
          )}

          <div className="ai-card rounded-lg p-6" data-testid="mrp-ai-insight">
            <div className="flex items-center gap-2 mb-2 text-[var(--ai-accent)]">
              <Sparkles className="w-4 h-4" />
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold">AI MRP Insight</div>
            </div>
            <div className="text-sm text-slate-700 leading-relaxed">{result.ai_insight || "No AI insight available."}</div>
          </div>

          {/* End-to-end requirements matrix */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>End-to-End Inventory & Net Requirement</h3>
                <p className="text-xs text-slate-500 mt-0.5">Net Req = Gross − (Raw Material + Machine Shop + Impregnation + Fabrication + Powder Coating + Semi Finished + Assembly + Testing + Final Inspection + Finished Goods + On-Order Incoming)</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div>
                  <Label htmlFor="mrp-part-number-filter" className="text-[10px] uppercase text-slate-500">Filter by Part Number</Label>
                  <div className="relative mt-0.5">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <Input
                      id="mrp-part-number-filter"
                      value={partNumberFilter}
                      onChange={(e) => setPartNumberFilter(e.target.value)}
                      placeholder="Type a part number…"
                      className="pl-8 w-56 h-9"
                      data-testid="mrp-part-number-filter"
                    />
                    {partNumberFilter && (
                      <button
                        onClick={() => setPartNumberFilter("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider text-slate-400 hover:text-slate-700"
                        data-testid="mrp-part-number-filter-clear"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                {STAGE_FILTERS.map((f) => (
                  <Button
                    key={f.key}
                    size="sm"
                    variant={typeFilter === f.key ? "default" : "outline"}
                    onClick={() => setTypeFilter(f.key)}
                    className={typeFilter === f.key ? "bg-blue-600 hover:bg-blue-700" : ""}
                    data-testid={`type-filter-${f.key.toLowerCase()}`}
                  >
                    {f.label}
                    <span className="ml-1.5 text-[10px] opacity-70">
                      {f.key === "ALL" ? result.material_requirements.length : result.material_requirements.filter((r) => (f.getQty(r) || 0) !== 0).length}
                    </span>
                  </Button>
                ))}
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="mrp-reqs-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
                    <th className="py-2 px-3">Code</th>
                    <th className="py-2 px-3">Item</th>
                    <th className="py-2 px-3">Type</th>
                    <th className="py-2 px-3 text-right">Gross Req</th>
                    <th className="py-2 px-3 text-right bg-amber-50/60">Raw Material</th>
                    {STAGE_COLS.map((c) => (
                      <th key={c.key} className="py-2 px-3 text-right bg-blue-50/60 whitespace-nowrap">{c.label}</th>
                    ))}
                    <th className="py-2 px-3 text-right bg-emerald-50/60">Finished Goods</th>
                    <th className="py-2 px-3 text-right bg-orange-50/60">Rework</th>
                    <th className="py-2 px-3 text-right bg-indigo-50/60">
                      <span className="inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" /> On-Order In</span>
                    </th>
                    <th className="py-2 px-3 text-right bg-rose-50/60">
                      <span className="inline-flex items-center gap-1"><TrendingDown className="w-3 h-3" /> On-Order Out</span>
                    </th>
                    <th className="py-2 px-3 text-right bg-purple-50/60" title="Stock reserved against open Sales Order — not free supply">Reserved</th>
                    <th className="py-2 px-3 text-right" title="Minimum stock level — added to demand when planning">Min Level</th>
                    <th className="py-2 px-3 text-right">Net Req</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => (
                    <tr key={r.item_id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`mrp-row-${r.code}`}>
                      <td
                        className="py-2 px-3 mono text-slate-700"
                        title={
                          (r.stores || []).length > 0
                            ? `Every store with stock:\n${r.stores.map((s) => `${s.name}: ${s.qty}`).join("\n")}`
                            : "No stock in any store"
                        }
                      >
                        {r.code}
                      </td>
                      <td className="py-2 px-3 text-slate-800">{r.name}</td>
                      <td className="py-2 px-3">
                        <TypePill t={r.type} />
                      </td>
                      <td className="py-2 px-3 mono text-right">{r.gross_requirement}</td>
                      <td className="py-2 px-3 mono text-right bg-amber-50/40">{r.rm_store}</td>
                      {STAGE_COLS.map((c) => (
                        <td key={c.key} className="py-2 px-3 mono text-right bg-blue-50/40">{r.stage_qty?.[c.key] ?? 0}</td>
                      ))}
                      <td className="py-2 px-3 mono text-right bg-emerald-50/40">{r.fg_store}</td>
                      <td className="py-2 px-3 mono text-right bg-orange-50/40">{r.rework_store}</td>
                      <td className="py-2 px-3 mono text-right bg-indigo-50/40">
                        {r.on_order_incoming || 0}
                        {(r.open_pos || []).length > 0 && (
                          <div className="text-[9px] text-indigo-700 mt-0.5">
                            {r.open_pos.slice(0, 2).map((p) => p.po_code).join(", ")}
                            {r.open_pos.length > 2 && ` +${r.open_pos.length - 2}`}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3 mono text-right bg-rose-50/40">
                        {r.on_order_outgoing || 0}
                        {(r.open_sos || []).length > 0 && (
                          <div className="text-[9px] text-rose-700 mt-0.5">
                            {r.open_sos.slice(0, 2).map((s) => s.so_code).join(", ")}
                            {r.open_sos.length > 2 && ` +${r.open_sos.length - 2}`}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3 mono text-right bg-purple-50/40" data-testid={`mrp-reserved-${r.code}`}>{r.reserved_qty || 0}</td>
                      <td className="py-2 px-3 mono text-right" data-testid={`mrp-min-${r.code}`}>
                        {r.min_stock_level || "—"}
                        {(r.stock_status === "below_min" || r.stock_status === "at_min") && (
                          <div className={`text-[9px] mt-0.5 font-semibold ${r.stock_status === "below_min" ? "text-red-600" : "text-amber-600"}`}>
                            {r.stock_status === "below_min" ? "Below min" : "At min"}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3 mono text-right">
                        <span className={r.net_requirement > 0 ? "text-red-600 font-bold" : "text-emerald-600 font-semibold"}>
                          {r.net_requirement}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {filteredRows.length === 0 && (
                    <tr><td colSpan={20} className="py-8 text-center text-sm text-slate-500">No items in this view.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-baseline justify-between">
              <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Planned Orders</h3>
              <span className="text-xs text-slate-500">{result.planned_orders.length} orders</span>
            </div>
            <table className="w-full text-sm" data-testid="mrp-planned-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
                  <th className="py-2 px-4">Code</th><th className="py-2 px-4">Name</th>
                  <th className="py-2 px-4">Type</th><th className="py-2 px-4 text-right">Qty</th>
                </tr>
              </thead>
              <tbody>
                {result.planned_orders.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100">
                    <td className="py-2 px-4 mono">{p.code}</td>
                    <td className="py-2 px-4 text-slate-700">{p.name}</td>
                    <td className="py-2 px-4">
                      <span className={`text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full
                        ${p.type === "make" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>
                        {p.type}
                      </span>
                    </td>
                    <td className="py-2 px-4 mono font-semibold text-right">{p.qty}</td>
                  </tr>
                ))}
                {result.planned_orders.length === 0 && (
                  <tr><td colSpan={4} className="py-6 text-center text-sm text-slate-500">No planned orders — all demand is covered by inventory + on-order incoming.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const TONE = {
  slate:   "bg-slate-50 border-slate-200 text-slate-700",
  amber:   "bg-amber-50 border-amber-200 text-amber-800",
  blue:    "bg-blue-50 border-blue-200 text-blue-800",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  indigo:  "bg-indigo-50 border-indigo-200 text-indigo-800",
  rose:    "bg-rose-50 border-rose-200 text-rose-800",
  red:     "bg-red-50 border-red-300 text-red-800",
  orange:  "bg-orange-50 border-orange-200 text-orange-800",
};

function TotalCard({ icon: Icon, label, value, tone = "slate", strong = false }) {
  return (
    <div className={`rounded-md border px-3 py-2.5 ${TONE[tone]}`} data-testid={`total-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] opacity-80">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`mono mt-1 ${strong ? "text-2xl font-extrabold" : "text-xl font-bold"}`}>
        {value ?? 0}
      </div>
    </div>
  );
}

// Type-to-search combobox for picking the FG/SFG demand item, mirroring the
// LineItemPicker used on Quotations / Transfer Orders for consistency.
function FgItemPicker({ value, selected: selectedProp, items, onPick, testId }) {
  const [open, setOpen] = useState(false);
  // Falls back to looking the value up in `items` when the caller doesn't pass
  // `selected` explicitly, so the trigger label still resolves even if the
  // active row's item has been filtered out of `items` by the search box.
  const selected = selectedProp ?? items.find((it) => it.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between h-9 font-normal" data-testid={testId}>
          <span className="truncate text-left">{selected ? `${selected.code} — ${selected.name}` : "Select finished good…"}</span>
          <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[420px]" align="start">
        <Command>
          <CommandInput placeholder="Search by item code or name…" data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>No items found</CommandEmpty>
            <CommandGroup>
              {items.map((it) => (
                <CommandItem
                  key={it.id}
                  value={`${it.code} ${it.name}`}
                  onSelect={() => { onPick(it.id); setOpen(false); }}
                  data-testid={`${testId}-opt-${it.code}`}
                >
                  <Check className={`w-3.5 h-3.5 mr-2 ${value === it.id ? "opacity-100" : "opacity-0"}`} />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{it.code}</span>
                    <span className="text-[11px] text-slate-500 truncate">{it.name}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function TypePill({ t }) {
  const s = t === "FG" ? "bg-emerald-50 text-emerald-700" :
            t === "SFG" ? "bg-blue-50 text-blue-700" :
            "bg-amber-50 text-amber-700";
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${s}`}>{t}</span>;
}
